import { useEffect, useState } from 'react';

import { useCustomAmountInput } from '@/components/Modals/TopUpModal/useCustomAmountInput';
import { SubscriptionPlan } from '@/components/SubscriptionCard/SubscriptionCard.utils';
import { formatMoney } from '@/Utils/helpers';
import { useCurrentUser } from '@/Utils/stripeHooks/useCurrentUser';
import { useTopUp } from '@/Utils/stripeHooks/useTopUp';

type Option = {
  id: string;
  value: number | string;
};

type AmountOptions = {
  minDeposit: number;
  options: Option[];
};

const plansTopUpAmountOptions: Record<
  Exclude<SubscriptionPlan, 'Free'>,
  AmountOptions
> = {
  'Proficient Pioneer': {
    minDeposit: 0,
    options: [
      { id: 'proficient-pioneer-option1', value: 5000 },
      { id: 'proficient-pioneer-option2', value: 25000 },
    ],
  },
  'Legendary Explorer': {
    minDeposit: 25000,
    options: [
      { id: 'legendary-explorer-option1', value: 25000 },
      { id: 'legendary-explorer-option2', value: 50000 },
    ],
  },
  'Galactic Adventurer': {
    minDeposit: 50000,
    options: [
      { id: 'galactic-adventurer-option1', value: 50000 },
      { id: 'galactic-adventurer-option2', value: 100000 },
    ],
  },
  'Nexus Elite': {
    minDeposit: 100000,
    options: [
      { id: 'nexus-elite-option1', value: 100000 },
      { id: 'nexus-elite-option2', value: 150000 },
    ],
  },
};

export const useTopUpModal = () => {
  const { data: user } = useCurrentUser();

  const {
    rawCustomAmountInputValue,
    formattedCustomAmountInputValue,
    handleCustomAmountInputChange,
  } = useCustomAmountInput();

  const {
    mutateAsync: topUp,
    isLoading: isTopUpLoading,
    error: topUpErrorResponse,
  } = useTopUp({
    onSuccess: async (res) => {
      if ('client_secret' in res) {
        localStorage.setItem(
          'client_secret_checkout_top_up',
          res.client_secret,
        );

        window.location.href = '/account/top_up_checkout';
      }
    },
  });

  const handleTopUpClick = async () => {
    const amountToSend = isCustomAmountOptionSelected
      ? rawCustomAmountInputValue
      : (selectedAmountOption?.value as number);

    if (!amountToSend) return;

    await topUp({
      amount: amountToSend,
    });
  };

  const topUpError =
    topUpErrorResponse && 'error' in topUpErrorResponse
      ? topUpErrorResponse.error
      : null;

  const [selectedAmountOption, setSelectedAmountOption] =
    useState<Option | null>(null);

  useEffect(() => {
    if (user && user.subscription?.plan_name) {
      const userPlan = user.subscription.plan_name;
      const defaultOption =
        plansTopUpAmountOptions[userPlan]?.options[0] || null;
      setSelectedAmountOption(defaultOption);
    }
  }, [user]);

  const userPlanAmounts =
    user?.subscription?.plan_name &&
    plansTopUpAmountOptions[user.subscription.plan_name];

  const amountOptions = userPlanAmounts
    ? userPlanAmounts.options.map((option) => ({
        id: option.id,
        value: formatAmount(Number(option.value)),
      }))
    : [];

  const minDepositFormatted = formatAmount(userPlanAmounts?.minDeposit || 0);
  const customAmountInputError = null;

  const showMinimumTopUpAmountWarning =
    user?.subscription?.plan_name &&
    user.subscription.plan_name !== 'Proficient Pioneer';

  const isCustomAmountOptionSelected = selectedAmountOption?.id === 'custom';

  const handleSelectAmountOption = (option: Option) => {
    if (userPlanAmounts) {
      setSelectedAmountOption(
        userPlanAmounts.options.find((o) => o.id === option.id) || null,
      );
    }
  };

  const handleSelectCustomAmountOption = () =>
    setSelectedAmountOption({ id: 'custom', value: 0 });

  return {
    user,
    topUpError,
    isTopUpLoading,
    formattedCustomAmountInputValue,
    customAmountInputError,
    minDepositFormatted,
    selectedAmountOption,
    amountOptions,
    showMinimumTopUpAmountWarning,
    isCustomAmountOptionSelected,
    handleCustomAmountInputChange,
    handleSelectAmountOption,
    handleSelectCustomAmountOption,
    handleTopUpClick,
  };
};

const formatAmount = (amount: number): string => {
  return formatMoney(amount, 'USD', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};
